<template>
  <div class="exchange-item">
    <!-- TODO consider to move this one level up outside of the for loop -->
    <confirm-modal
      v-if="confirmModal"
      @confirmClose="confirmClose"
      @confirmOk="deleteExchange(type)"
    >
      <h3>{{ $t("confirm-delete") }}</h3>
      <p
        v-if="type === 'incoming'"
        v-html="$t('confirm-incoming-modal-delete')"
      ></p>
      <p v-else v-html="$t('confirm-outgoing-modal-delete')"></p>
    </confirm-modal>
    <header>
      <div>
        <button type="button" @click="expand = !expand" class="row-toggler">
          <arrow-icon
            width="18"
            height="18"
            :direction="expand ? 'up' : 'down'"
          ></arrow-icon>
        </button>
        <p
          v-if="type === 'incoming'"
          v-html="
            $t('dashboard-incoming-exchange-received', {
              count: item.files.length,
              sender: item.sender.email
            })
          "
        ></p>
        <p
          v-else
          v-html="
            $t('dashboard-outgoing-exchange-sent', {
              count: item.files.length,
              receiver: item.receivers[0].email,
              outgoingOtherReceivers
            })
          "
        ></p>
      </div>
      <div>
        <p class="date">
          {{ item.sharedAt | dateShared }}
        </p>
        <p v-if="item.encrypted">
          <lock-icon width="14" height="13" fillColor="#81869e"></lock-icon>
        </p>
        <p v-else>
          <lock-icon
            width="14"
            height="13"
            fillColor="#d1d5da"
            type="unlocked"
          ></lock-icon>
        </p>
        <p v-if="expired" class="expired">
          <button class="white" disabled>
            {{ $t("dashboard-exchange-expired") }}
          </button>
        </p>
        <p v-else>
          <button
            v-if="type === 'incoming'"
            type="button"
            class="primary"
            @click.prevent="downloadFile()"
          >
            <span v-if="item.filesReceived" class="flex">
              <check-icon width="14" height="14"></check-icon>
            </span>
            <span v-else class="flex">
              <download-icon width="14" height="14"></download-icon>
            </span>
            {{ $t("dashboard-files-outgoing-download") }}
          </button>
          <button
            v-else
            type="button"
            class="default"
            @click="shareAgain(item.id)"
          >
            <share-icon width="20" height="15"></share-icon>
            {{ $t("dashboard-files-outgoing-share") }}
          </button>
        </p>
        <p>
          <button
            class="delete-btn"
            type="button"
            @click.prevent="confirmModal = true"
          >
            <trash-icon width="14" height="16"></trash-icon>
          </button>
        </p>
      </div>
    </header>
    <div v-show="expand">
      <div v-for="file in item.files" :key="file.id" class="row-content">
        <p :title="file.filename">
          <strong>{{ file.filename }}</strong>
        </p>
        <p>
          <file-icon width="10" height="13"></file-icon>
          {{ file.filename | prettyExtention }}
        </p>
        <p>{{ file.size | convertSize }} kb</p>
        <p v-if="expired">{{ $t("dashboard-exchange-expired") }}</p>
        <p v-else>
          {{ $t("dashboard-expires") }} {{ item.validUntil | expireDays }}
        </p>
      </div>
      <footer v-if="item.receivers.length">
        <strong>{{ $t("dashboard-shared-with") }}</strong>
        <email-tag
          v-for="(receiver, index) in item.receivers"
          :receiver="receiver"
          :key="index"
          :type="type"
          :tooltipTitle="receiver"
        ></email-tag>
      </footer>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapActions } from "vuex";
import EmailTag from "@/components/EmailTag";
import ShareIcon from "@/components/icons/ShareIcon";
import ArrowIcon from "@/components/icons/ArrowIcon";
import DownloadIcon from "@/components/icons/DownloadIcon";
import CheckIcon from "@/components/icons/CheckIcon";
import FileIcon from "@/components/icons/FileIcon";
import LockIcon from "@/components/icons/LockIcon";
import TrashIcon from "@/components/icons/TrashIcon";
import ConfirmModal from "@/components/ConfirmModal";

export default {
  name: "ExchangeItem",
  props: ["item", "type"],
  components: {
    EmailTag,
    ShareIcon,
    ArrowIcon,
    DownloadIcon,
    FileIcon,
    TrashIcon,
    ConfirmModal,
    CheckIcon,
    LockIcon
  },
  data() {
    return {
      expand: false,
      confirmModal: false
    };
  },
  computed: {
    expired() {
      const validUntil = new Date(this.item.validUntil);
      const now = new Date();
      return validUntil < now;
    },
    outgoingOtherReceivers() {
      if (this.type === "outgoing" && this.item.receivers.length > 1) {
        let receivers = [...this.item.receivers];
        const count = receivers.length - 1;
        const otherReceivers = receivers
          .slice(1)
          .map(receiver => receiver.email.split("@")[0]);
        return this.$t("dashboard-other-receivers-tooltip", {
          count,
          otherReceivers
        });
      }
    }
  },
  methods: {
    ...mapActions("common", ["setLoading", "showModal"]),
    ...mapActions("exchange", ["getOutgoingExchanges", "getIncomingExchanges"]),
    downloadFile() {
      axios({
        url: `/incoming/exchange/raw/${encodeURIComponent(this.item.code)}`,
        method: "GET",
        responseType: "blob"
      })
        .then(res => {
          this.setLoading(true);
          const regex = /filename="(.*?)"/;
          const str = res.headers["content-disposition"];
          let fileName = regex.exec(str)[1];
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          if (navigator.msSaveBlob) {
            return navigator.msSaveBlob(new Blob([res.data]), fileName);
          } else {
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
          }
          Promise.all([
            this.getOutgoingExchanges(),
            this.getIncomingExchanges()
          ])
            .then()
            .catch(err => {
              this.setLoading(false);
              this.showModal({
                title: err.errorCode
              });
            });
        })
        .catch(err => {
          this.showModal({
            title: err.errorCode
          });
        })
        .finally(() => this.setLoading(false));
    },
    deleteExchange(type) {
      if (type === "incoming") {
        this.deleteIncomingExchange(this.item.code);
      } else {
        this.deleteOutgoingExchange(this.item.id);
      }
    },
    deleteIncomingExchange(code) {
      this.setLoading(true);
      this.$store
        .dispatch("exchange/deleteIncomingExchange", code)
        .then(this.setLoading(false))
        .catch(err => {
          this.setLoading(false);
          this.showModal({
            title: err.errorCode
          });
        });
    },
    deleteOutgoingExchange(id) {
      this.setLoading(true);
      this.$store
        .dispatch("exchange/deleteOutgoingExchange", id)
        .then(this.setLoading(false))
        .catch(err => {
          this.setLoading(false);
          this.showModal({
            title: err.errorCode
          });
        });
    },
    shareAgain(id) {
      this.$emit("reShareExchange", id);
    },
    confirmClose() {
      this.confirmModal = false;
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../../../../assets/styles/variables";
@import "../../../../../assets/styles/tooltip";

.exchange-item {
  font-size: 14px;
}

.incoming-exchange button {
  min-width: 180px;
}

.outgoing-exchange button {
  min-width: 160px;
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 18px;
  > div {
    display: flex;
    align-items: center;
  }
  p {
    margin-top: 0;
    margin-bottom: 0;
    margin-left: 15px;
  }
  .tooltip:before {
    min-width: 100px;
    max-width: 350px;
    word-wrap: break-word;
  }
  .delete-btn,
  .row-toggler {
    min-width: auto;
    svg {
      margin: 0;
    }
  }

  .delete-btn {
    background: none;
    border: none;

    padding: 10px;
  }
}

.row-toggler {
  width: 26px;
  height: 26px;
  background-color: #f7f7f7;
  border: 1px solid $middle-gray;
  border-radius: 100%;
  padding: 5px;
  &:hover {
    background-color: darken(#f7f7f7, 5%);
  }
}

.row-content {
  display: flex;
  justify-content: space-between;
  padding: 0 18px;
  background-color: $light-gray;
  > p {
    width: 33%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    &:not(:last-child) {
      padding-right: 15px;
    }
    &:last-child {
      text-align: right;
      color: $placeholder-color;
    }
  }
}

.row-content,
.exchange-item footer {
  border-top: 1px solid $middle-gray;
}

.exchange-item {
  footer {
    padding: 5px 15px;
    background: white;
    strong {
      margin-right: 10px;
    }
  }
  .expired {
    button {
      color: $placeholder-color;
      text-transform: uppercase;
      border: 1px dashed $middle-gray;
      font-family: Gotham-book;
    }
  }
}
</style>
